import { useState, useEffect, useRef, useCallback } from 'react';
import CONFIG from '../config';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  loadColorResultsMeta,
  loadSlideColorResult,
  subscribeToColorAnalysisProgress
} from '../services/colorResultService';
import { fetchWithAuth } from '../utils/api';
import { downloadFile } from '../utils/downloadUtils';
import { loadColorDraft, deleteColorDraft } from '../services/draftService';

export const useColorAnalysis = (slides, selectedPpt, userId, colors, showAlertModal, showConfirmModal, setIsModifying, onAnalysisComplete, t) => {
  // 전체 슬라이드 색상 검사 관련 상태
  const [allSlidesColorResults, setAllSlidesColorResults] = useState([]);
  // Draft 존재 여부
  const [hasColorDraft, setHasColorDraft] = useState(false);
  // 로드된 Draft 데이터 (모달 열 때 확인 후 적용)
  const [loadedColorDraft, setLoadedColorDraft] = useState(null);
  // [{slide_index, violations_count, has_violations, thumbnail_url}, ...]
  const [colorCheckStatus, setColorCheckStatus] = useState(''); // 'checking', 'completed', 'no_violations', 'error'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [checkProgress, setCheckProgress] = useState({ status: '', percent: 0, completed: false });

  // 슬라이드 크기 정보
  const [slideSize, setSlideSize] = useState({ width: 0, height: 0 });

  // 총 위반 개수 (메타데이터에서 로드)
  const [totalViolations, setTotalViolations] = useState(0);

  // 결과 로딩 상태
  const [isLoadingResults, setIsLoadingResults] = useState(false);

  // 리스너 참조 저장 (탭 전환/PPT 전환 시 복구용)
  const unsubscribeRef = useRef(null);
  // 콜백 ref (의존성 루프 방지)
  const callbacksRef = useRef({ showAlertModal, showConfirmModal, onAnalysisComplete, t });
  callbacksRef.current = { showAlertModal, showConfirmModal, onAnalysisComplete, t };

  // 배열 비교 유틸리티 함수
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  };

  // 진행상황 업데이트 핸들러
  const handleProgressUpdate = useCallback(async (progressData) => {
    const { showAlertModal, showConfirmModal, onAnalysisComplete, t } = callbacksRef.current;

    // 에러 발생 시
    if (progressData.error) {
      console.log('[useColorAnalysis] 검사 에러:', progressData.error);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setIsAnalyzing(false);
      setColorCheckStatus('error');
      setCheckProgress({ status: '', percent: 0, completed: false });

      showAlertModal(t('hooks.color.errorDuringCheck', { error: progressData.error }), 'error');
      return;
    }

    // 진행상황 업데이트
    setCheckProgress({
      status: progressData.status || '',
      percent: progressData.percent || 0,
      completed: progressData.completed || false
    });

    // 완료 시 결과 로드 + 모달 표시
    if (progressData.completed) {
      console.log('[useColorAnalysis] 검사 완료, 결과 로드 중...');

      // 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      try {
        // 메타데이터만 로드 (개별 슬라이드는 모달에서 lazy loading)
        const metaData = await loadColorResultsMeta(selectedPpt);

        if (metaData) {
          // 슬라이드 결과는 빈 배열로 시작 (모달에서 lazy loading)
          setAllSlidesColorResults([]);

          if (metaData.slide_width && metaData.slide_height) {
            setSlideSize({
              width: metaData.slide_width,
              height: metaData.slide_height
            });
          }

          // 총 위반 개수 설정
          const violations = metaData.total_violations || 0;
          setTotalViolations(violations);

          const hasViolations = violations > 0;
          setColorCheckStatus(hasViolations ? 'completed' : 'no_violations');

          // 모달 표시 전에 스피너 중지
          setIsAnalyzing(false);

          // 완료 모달 표시 (비동기 처리)
          showConfirmModal(
            t('hooks.color.checkComplete', { count: violations })
          ).then(async (confirmed) => {
            if (confirmed && onAnalysisComplete) {
              // 첫 번째 슬라이드 결과 미리 로드 (모달에서 바로 보이도록)
              try {
                const firstSlideResult = await loadSlideColorResult(selectedPpt, 0);
                if (firstSlideResult) {
                  setAllSlidesColorResults([firstSlideResult]);
                }
              } catch (loadError) {
                console.error('[useColorAnalysis] 첫 번째 슬라이드 로드 오류:', loadError);
              }
              onAnalysisComplete('color');
            }
          });
        } else {
          setIsAnalyzing(false);
        }
      } catch (error) {
        console.error('[useColorAnalysis] 결과 로드 오류:', error);
        setColorCheckStatus('error');
        setIsAnalyzing(false);
      }
    }
  }, [selectedPpt]);

  // PPT 변경 시 상태 초기화, 기존 결과 로드, 진행 중인 검사 복구
  useEffect(() => {
    const loadExistingResults = async () => {
      // 기존 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      if (!selectedPpt || !userId) {
        setAllSlidesColorResults([]);
        setColorCheckStatus('');
        setCheckProgress({ status: '', percent: 0, completed: false });
        setIsAnalyzing(false);
        return;
      }

      try {
        setIsLoadingResults(true);

        // 1. 진행 중인 검사 확인
        const progressRef = doc(db, 'PPT_FILE', selectedPpt, 'color_results', 'analysis_progress');
        const progressSnap = await getDoc(progressRef);

        if (progressSnap.exists()) {
          const progressData = progressSnap.data();

          // 진행 중인 검사가 있는 경우 (완료되지 않고, 에러도 없는 경우)
          if (!progressData.completed && !progressData.error && progressData.status) {
            console.log('[useColorAnalysis] 진행 중인 색상 검사 발견, 리스너 연결');

            setIsAnalyzing(true);
            setColorCheckStatus('checking');
            setCheckProgress({
              status: progressData.status || '',
              percent: progressData.percent || 0,
              completed: false
            });

            // 리스너 시작
            unsubscribeRef.current = subscribeToColorAnalysisProgress(selectedPpt, handleProgressUpdate);
            setIsLoadingResults(false);
            return;
          }
        }

        // 2. 메타데이터만 로드 (lazy loading - 개별 슬라이드는 모달에서 로드)
        const metaData = await loadColorResultsMeta(selectedPpt);

        if (metaData && metaData.total_slides > 0) {
          // 슬라이드 결과는 빈 배열로 시작 (모달에서 lazy loading)
          setAllSlidesColorResults([]);

          if (metaData.slide_width && metaData.slide_height) {
            setSlideSize({
              width: metaData.slide_width,
              height: metaData.slide_height
            });
          }

          // 총 위반 개수 설정
          setTotalViolations(metaData.total_violations || 0);

          // 브랜드키트 색상이 변경되었는지 확인
          const savedBrandColors = metaData.brand_colors || [];
          const currentColors = colors || [];

          const colorsChanged = !arraysEqual(
            savedBrandColors.map(c => c.trim().toLowerCase()).sort(),
            currentColors.map(c => c.trim().toLowerCase()).sort()
          );

          if (colorsChanged) {
            setColorCheckStatus('outdated');
          } else {
            // total_violations로 상태 결정 (개별 슬라이드 로드 없이)
            const hasViolations = metaData.total_violations > 0;
            setColorCheckStatus(hasViolations ? 'completed' : 'no_violations');
          }

          // 3. Draft 로드 (검사 결과가 있는 경우에만) - 바로 적용하지 않고 저장만
          const draft = await loadColorDraft(selectedPpt, userId);
          if (draft && Object.keys(draft).length > 0) {
            setHasColorDraft(true);
            setLoadedColorDraft(draft);
            console.log('[useColorAnalysis] 칼라 draft 로드됨:', Object.keys(draft).length, '개 슬라이드');
          } else {
            setHasColorDraft(false);
            setLoadedColorDraft(null);
          }
        } else {
          setAllSlidesColorResults([]);
          setColorCheckStatus('');
          setTotalViolations(0);
          setHasColorDraft(false);
          setLoadedColorDraft(null);
        }
      } catch (error) {
        console.error('[useColorAnalysis] 기존 결과 로드 오류:', error);
        setAllSlidesColorResults([]);
        setColorCheckStatus('');
        setHasColorDraft(false);
        setLoadedColorDraft(null);
      } finally {
        setIsLoadingResults(false);
      }
    };

    loadExistingResults();

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      if (unsubscribeRef.current) {
        console.log('[useColorAnalysis] 리스너 해제 (언마운트)');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [selectedPpt, userId, colors, handleProgressUpdate]);

  // 전체 슬라이드 색상 검사 함수 (비동기 방식)
  const analyzeAllSlidesColors = async () => {
    console.log('[DEBUG] analyzeAllSlidesColors 함수 호출됨');

    if (!selectedPpt || !userId) {
      showAlertModal(t('hooks.color.noDocOrUser'), 'error');
      return;
    }

    if (!colors || colors.length === 0) {
      showAlertModal(t('hooks.color.noBrandkitColors'), 'warning');
      return;
    }

    if (!slides || slides.length === 0) {
      showAlertModal(t('hooks.color.noSlides'), 'warning');
      return;
    }

    setIsAnalyzing(true);
    setColorCheckStatus('checking');
    setCheckProgress({ status: 'preparing', percent: 0, completed: false });

    // 새로운 분석 시작 시 기존 draft 삭제
    await deleteColorDraft(selectedPpt);
    setHasColorDraft(false);

    try {
      // 기존 리스너 해제
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      // API 호출 (즉시 응답)
      const response = await fetchWithAuth(`${CONFIG.CLOUD_FUNCTION_URL}/check_all_slides_colors`, {
        method: 'POST',
        body: JSON.stringify({
          doc_id: selectedPpt,
          userId: userId,
          brand_colors: colors
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      // 즉시 응답 수신 - 백그라운드 처리 시작됨
      if (data.success) {
        console.log('[useColorAnalysis] 색상 검사 시작됨:', data);

        // 리스너 시작 (완료까지 유지)
        unsubscribeRef.current = subscribeToColorAnalysisProgress(selectedPpt, handleProgressUpdate);
      }

    } catch (error) {
      console.error('색상 검사 시작 오류:', error);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      setColorCheckStatus('error');
      setCheckProgress({ status: '', percent: 0, completed: false });
      setIsAnalyzing(false);

      showAlertModal(error.message || t('hooks.color.errorGeneric'), 'error');
    }
  };

  // 결과 썸네일 클릭 시 모달 열기 (상위 컴포넌트에서 처리)
  const handleResultThumbnailClick = (slideIndex, onModalOpen) => {
    if (onModalOpen) {
      onModalOpen(slideIndex);
    }
  };

  // 현재 선택된 슬라이드의 검사 결과 가져오기
  const getCurrentSlideResult = (slideIndex) => {
    return allSlidesColorResults.find(result => result.slide_index === slideIndex);
  };

  // 특정 슬라이드 결과 lazy loading (결과가 없으면 로드)
  const loadSlideResultIfNeeded = useCallback(async (slideIndex) => {
    // 이미 로드된 경우 스킵
    const existing = allSlidesColorResults.find(r => r.slide_index === slideIndex);
    if (existing) return existing;

    if (!selectedPpt) return null;

    try {
      const result = await loadSlideColorResult(selectedPpt, slideIndex);
      if (result) {
        setAllSlidesColorResults(prev => {
          // 중복 체크
          if (prev.find(r => r.slide_index === slideIndex)) return prev;
          return [...prev, result];
        });
        return result;
      }
      return null;
    } catch (error) {
      console.error(`[useColorAnalysis] 슬라이드 ${slideIndex} 결과 로드 오류:`, error);
      return null;
    }
  }, [selectedPpt, allSlidesColorResults]);

  // PPT 수정하기 함수 (모달에서 호출)
  const handlePPTModification = async (modalSlideIndex, violationColorChanges, isMultiSlide = false) => {
    // isMultiSlide가 true면 violationColorChanges는 { slideIndex: { violationIndex: color } } 구조
    // isMultiSlide가 false면 기존과 동일 { violationIndex: color } 구조

    let modifications = [];

    if (isMultiSlide) {
      // 여러 슬라이드 처리
      Object.entries(violationColorChanges).forEach(([slideIndex, slideChanges]) => {
        Object.entries(slideChanges).forEach(([violationIndex, newColor]) => {
          // 6자리 검증
          const hexValue = newColor.replace('#', '');
          if (!/^[0-9A-Fa-f]{6}$/.test(hexValue)) {
            return; // 유효하지 않은 색상은 스킵
          }

          modifications.push({
            slide_index: parseInt(slideIndex),
            violation_index: parseInt(violationIndex),
            new_color: newColor.replace('#', '').toUpperCase()
          });
        });
      });
    } else {
      // 단일 슬라이드 처리 (기존 로직)
      // 유효한 변경사항이 있는지 확인
      const hasValidChanges = violationColorChanges && Object.entries(violationColorChanges).some(([_key, value]) => value && value.trim() !== '');

      if (!hasValidChanges) {
        showAlertModal(t('hooks.color.setColorFirst'), 'warning');
        return;
      }

      // 6자리 hex 코드 검증
      const invalidColors = Object.entries(violationColorChanges)
        .filter(([_key, value]) => value && value.trim() !== '')
        .filter(([_key, value]) => !/^[0-9A-Fa-f]{6}$/.test(value.replace('#', '')));

      if (invalidColors.length > 0) {
        const invalidValues = invalidColors.map(([_key, value]) => value).join(', ');
        showAlertModal(t('hooks.color.invalidColorCode', { invalidValues }), 'warning');
        return;
      }

      modifications = Object.entries(violationColorChanges).map(([violationIndex, newColor]) => ({
        slide_index: modalSlideIndex,
        violation_index: parseInt(violationIndex),
        new_color: newColor.replace('#', '').toUpperCase()
      }));
    }

    if (modifications.length === 0) {
      showAlertModal(t('hooks.color.noColorToModify'), 'warning');
      return;
    }

    // selectedPpt는 이미 docId
    try {
      // 로딩 상태 시작
      if (setIsModifying) setIsModifying(true);

      const requestData = {
        doc_id: selectedPpt,
        userId: userId,
        modifications: modifications
      };

      const response = await fetchWithAuth(`${CONFIG.CLOUD_FUNCTION_URL}/modify_slide_colors`, {
        method: 'POST',
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('hooks.color.modifyError'));
      }

      // 성공 메시지
      const slideCount = isMultiSlide
        ? Object.keys(violationColorChanges).length
        : 1;
      const message = t('hooks.color.modifySuccess', { slideCount, count: modifications.length });

      // 성공 시 다운로드 링크 제공
      if (data.download_url) {
        const downloadConfirm = await showConfirmModal(message);
        if (downloadConfirm) {
          // Blob 방식으로 다운로드 (Edge 호환)
          await downloadFile(data.download_url, data.filename);
        }
      } else {
        showAlertModal(data.message || t('hooks.color.modifyComplete'), 'success');
      }

      return true; // 성공 시 true 반환

    } catch (error) {
      console.error('PPT 수정 오류:', error);
      showAlertModal(error.message || t('hooks.color.modifyError'), 'error');
      return false; // 실패 시 false 반환
    } finally {
      // 로딩 상태 종료
      if (setIsModifying) setIsModifying(false);
    }
  };

  // Draft 적용 함수 (모달에서 확인 시 호출)
  const applyColorDraft = useCallback(() => {
    if (loadedColorDraft) {
      console.log('[useColorAnalysis] 칼라 draft 적용됨');
      return loadedColorDraft;
    }
    return null;
  }, [loadedColorDraft]);

  // Draft 초기화 함수 (모달에서 취소 시 호출)
  const clearColorDraft = useCallback(() => {
    setLoadedColorDraft(null);
    setHasColorDraft(false);
    console.log('[useColorAnalysis] 칼라 draft 초기화됨');
  }, []);

  return {
    allSlidesColorResults,
    colorCheckStatus,
    isAnalyzing,
    isLoadingResults,
    checkProgress,
    slideSize,
    totalViolations,
    hasColorDraft,
    loadedColorDraft,
    analyzeAllSlidesColors,
    handleResultThumbnailClick,
    getCurrentSlideResult,
    loadSlideResultIfNeeded,
    handlePPTModification,
    applyColorDraft,
    clearColorDraft
  };
};